import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { DateTime } from "luxon";
import {
  Alert,
  Row,
  Button,
  TextInput,
  Select,
  ResponsiveFlex,
  Tooltip,
} from "@narmi/design_system";
import { entities, modules } from "byzantine";
import { parsedRowsToBulkPayments } from "byzantine/src/dbbl/businessLogic/entities/achImport";
import { dollarsToCents } from "byzantine/src/dbbl/utils/money";
import {
  useMappedEntryDescription,
  MAX_MEMO_LENGTH,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import {
  MAX_BULK_TITLE_LENGTH,
  bulkPaymentSchema,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import type { BulkPaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import Account from "byzantine/src/Account";
import * as formAdapters from "../../../src/adapters";
import ComboAccountSelector from "../../ComboAccountSelector";
import AccountContext from "../../contexts/AccountContext";
import Container from "../../Container";
import HeaderWithSteps from "../../HeaderWithSteps";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { useACHImportContext } from "../ACHImportContext";

/**
 * Converts ACH import payment rows into BulkPaymentForm row entries.
 */
const convertImportRowsToBulkPayments = (
  rows: API.ACHImport.PaymentRow[],
  internalNote: string
): BulkPaymentRowType[] =>
  rows.map((row) => ({
    recipient: row.recipient ?? undefined,
    amount: dollarsToCents(row.amount as Dollars),
    description: row.description || internalNote || "",
    transactionType: row.transaction_type,
    scheduledDate: row.scheduled_date
      ? DateTime.fromISO(row.scheduled_date).toFormat("MM/dd/yyyy")
      : "",
  }));

const AdditionalDetailsScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { accounts } = useContext(AccountContext);
  const { nachaResponse, importType } = useACHImportContext();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const { submitForm, setValidationSchema } = form;
  const codes = modules.achPayments.useSECCodes();
  const { mappedOptions } = useMappedEntryDescription();

  const validAccounts = accounts.filter((account: Account) =>
    account.isValidAchTransferSource()
  );

  // Validate only top-level fields (not payments array)
  useEffect(() => {
    setValidationSchema(bulkPaymentSchema);
  }, [setValidationSchema]);

  // Redirect if no import data
  if (!nachaResponse) {
    window.location.assign(`/${PAYMENT_ACTIVITY_ROUTE}/upload`);
    return null;
  }

  const handleBack = () => {
    // Navigate to the explicit previous step rather than relying on browser
    // history (navigate(-1)), which loops because the user can reach this
    // screen via the "Back" button on the downstream payments table.
    const hasNewRecipients = nachaResponse.rows.some(
      (row) => row.non_recipient_destination !== null
    );
    if (hasNewRecipients) {
      navigate("../review-recipients");
    } else if (importType === "csv") {
      navigate("../map-columns");
    } else {
      // NACHA with no new recipients comes straight from the file upload step.
      navigate("..");
    }
  };

  const handleNext = async () => {
    const { success } = await submitForm();
    if (!success) return;

    // Convert import rows to BulkPaymentForm payments
    const payments =
      importType === "csv"
        ? parsedRowsToBulkPayments(
            nachaResponse.rows as API.ACHImport.CSVParsedRow[],
            "",
            form.values.description ?? ""
          )
        : convertImportRowsToBulkPayments(
            nachaResponse.rows,
            form.values.description ?? ""
          );
    form.setFieldValue("payments", payments);

    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/payments`);
  };

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        useDisplayFont
        headerText={l10n.getString(
          "file-upload-screen-header"
        )}
      />
      <div className="margin--top--l">
        <h4 className="fontSize--l nds-sans margin--bottom--xxs">
          {l10n.getString("ach-payment-payment-details-title")}
        </h4>
        <p className="margin--bottom--l">
          {l10n.getString("ach-import-payment-details-description")}
        </p>
        <ResponsiveFlex gapSize="l">
          <Row>
            <Row.Item>
              <TextInput
                label={l10n.getString("bulk-payment-title")}
                maxLength={MAX_BULK_TITLE_LENGTH}
                endContent={
                  <Tooltip
                    text={l10n.getString("bulk-payment-title-tooltip-copy")}
                    maxWidth="235px"
                  >
                    <span className="narmi-icon-info"></span>
                  </Tooltip>
                }
                {...formAdapters.input(form, "title")}
              />
            </Row.Item>
          </Row>

          <div className="margin--bottom--l">
            <Row>
              <Row.Item>
                {validAccounts.length === 0 ? (
                  <>
                    <div className="margin--bottom--s">
                      <Alert isActive kind="warn" isDismissable={false}>
                        {l10n.getString(
                          "bulk-payment-no-valid-source-account-alert"
                        )}
                      </Alert>
                    </div>
                    <TextInput
                      label={l10n.getString("label-account")}
                      disabled
                    />
                  </>
                ) : (
                  <ComboAccountSelector
                    label={l10n.getString("label-account")}
                    accounts={validAccounts}
                    isDestination={true}
                    showAddExternalAccountLink={false}
                    {...formAdapters.select(form, "fromAccount")}
                  />
                )}
              </Row.Item>
            </Row>
          </div>

          <div className="margin--bottom--l">
            <Row>
              <Row.Item>
                <Select
                  label={l10n.getString("label-payment-type")}
                  disabled={codes.length === 0}
                  {...formAdapters.select(form, "secCode")}
                >
                  {codes.map((code, index) => (
                    <Select.Item key={`sec_code_${index}`} value={code}>
                      {code}
                    </Select.Item>
                  ))}
                </Select>
              </Row.Item>
            </Row>
          </div>

          <div className="margin--bottom--l">
            <Row>
              <Row.Item>
                <Select
                  label={l10n.getString("label-entry-description")}
                  disabled={mappedOptions.length === 0}
                  {...formAdapters.select(form, "entryDesc")}
                >
                  {mappedOptions.map(({ display, value }) => (
                    <Select.Item key={value} value={value}>
                      {display}
                    </Select.Item>
                  ))}
                </Select>
              </Row.Item>
            </Row>
          </div>

          <Row>
            <Row.Item>
              <TextInput
                label={l10n.getString("label-internal-note-optional")}
                maxLength={MAX_MEMO_LENGTH}
                {...formAdapters.input(form, "description")}
              />
            </Row.Item>
          </Row>
        </ResponsiveFlex>
      </div>
      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("recipient-review-back")}
              onClick={handleBack}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleNext}
            />
          </Row.Item>
        </Row>
      </div>
    </Container>
  );
};

export default AdditionalDetailsScreen;
